/** @jsxImportSource @emotion/react */
/* eslint-disable jsx-a11y/anchor-is-valid, react/no-unknown-property */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import EditIcon from '@mui/icons-material/Edit';
import IconTileRadioButton from '@IconTileRadioButton';
import ListLayout from '@ListLayout';
import RadioButton from '@RadioButton';
import RadioGroup from '@RadioGroup';
import TileLayout from '@TileLayout';
import { useEffect, useReducer, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getFirstEditableComponentOnPage } from '../../Helpers/components';
import {
  clearConstraintsValidations,
  clearSelectedOptionsAndValue,
  selectOptionAndUpdateValue,
  setComponentValidationStatus,
  setIsCheckedByDefault,
  updateOtherValueAndValue,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { reopenField } from '../../Lib/redux/slices/progressiveDisplay';
import { setComponentIdToReceiveFocus } from '../../Lib/redux/slices/persistKeyboard';
import QuestionText from '../QuestionText/QuestionText';
import {
  isIconForEveryOption,
  swapS3DomainWithPublicAssetsDomain,
  getIconHeight,
} from '../__helpers__/component.helpers';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import useGetOptionHeights from '../../Hooks/useGetOptionHeights/useGetOptionHeights';
import makeRadioInputStyles from './makeRadioInputStyles';
import { ComponentTypesToReceiveFocusOnPageEnter } from '../../Data/Components';

const DEFAULT_EDIT_LABEL = 'Edit';

const getSelectedOptionLabel = ({ options, selectedOptions, otherValue }) => {
  const selectedId = selectedOptions?.[0];
  if (selectedId === undefined) return '';
  if (selectedId === 'other-option') return otherValue;
  return (
    options.find((option) => String(option.id) === String(selectedId))?.value ??
    ''
  );
};

const RadioInput = ({
  autoAdvance,
  componentId,
  subheading,
  iFrameId,
  formId,
  isCollapsed = false,
  isInProgressiveChain = false,
  editLabel = DEFAULT_EDIT_LABEL,
}) => {
  const dispatch = useDispatch();
  const [selectionCount, triggerSelection] = useReducer((x) => x + 1, 0);
  // A pointer pressed inside the option group blurs the Other field before
  // the click lands, so that blur must not commit the text.
  const isPointerDownInGroup = useRef(false);
  const hasCommittedOther = useRef(false);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const components = useSelector((store) => store.components);
  const { pages } = useSelector((store) => store.paging);
  const supplierMatches = useSelector((store) => store.supplierMatches);
  const showSmartMatchMeter = useSelector(
    (store) => store.features.showSmartMatchMeter
  );
  const showSmartMatchMeterV2 = useSelector(
    (store) => store.features.showSmartMatchMeterV2
  );
  const { isIos, isAndroid } = useSelector((store) => store.formConfig);
  const [s3EnvDomain, setS3EnvDomain] = useState(
    process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL
  );
  const { options, hiddenInChameleon, otherValue } = component.input;
  const { text, forceListLayout } = component.component;
  const allOptionsHaveIcons = isIconForEveryOption(options);
  const isTiled = allOptionsHaveIcons && !forceListLayout;
  const theme = useTheme();
  const styles = makeRadioInputStyles(theme);
  let { selectedOptions } = component.input;
  const Layout = isTiled ? TileLayout : ListLayout;

  const { tallestOptionHeight, optionRefs } = useGetOptionHeights({
    options,
    allOptionsHaveIcons,
    componentPage: component?.component?.page,
    isTiled,
  });

  const radioButtonExperimentName = useGetExperimentName({
    componentId,
    componentName: 'RadioButton',
  });
  const iconTileRadioButtonExperimentName = useGetExperimentName({
    componentId,
    componentName: 'IconTileRadioButton',
  });
  const radioGroupExperimentName = useGetExperimentName({
    componentId,
    componentName: 'RadioGroup',
  });
  const layoutExperimentName = useGetExperimentName({
    componentId,
    componentName: isTiled ? 'TileLayout' : 'ListLayout',
  });

  const handleInputChange = ({ id, value }) => {
    hasCommittedOther.current = false;
    batch(() => {
      dispatch(
        clearConstraintsValidations({ componentId: component.component.id })
      );
      dispatch(
        updateOtherValueAndValue({
          componentId,
          optionId: id,
          value,
        })
      );
    });
  };

  const validateAnswer = () => {
    batch(() => {
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  // In a progressive chain the Other text field counts as answered once the
  // customer commits what they typed (Enter or leaving the field), so the chain
  // does not treat the first keystroke as the answer. Outside a chain the page's
  // own navigation validates it, as it always has.
  // Enter commits and then the field blurs itself, so the second call must
  // not validate (and fire the answer side effects) again. Typing or choosing
  // an option starts a new answer.
  const commitOtherAnswer = () => {
    if (!isInProgressiveChain || hasCommittedOther.current) return;
    if (selectedOptions?.[0] !== 'other-option' || !otherValue) return;
    hasCommittedOther.current = true;
    validateAnswer();
  };

  const handleGroupPointerUp = () => {
    isPointerDownInGroup.current = false;
  };

  // The pointer may be released outside the group (a drag), where the
  // container never hears about it, so the document resets the flag too.
  const handleGroupPointerDown = () => {
    isPointerDownInGroup.current = true;
    document.addEventListener('pointerup', handleGroupPointerUp, {
      once: true,
    });
    document.addEventListener('pointercancel', handleGroupPointerUp, {
      once: true,
    });
  };

  // Any blur bubbling to the option container commits Other text
  // (commitOtherAnswer ignores every other case), unless focus is only moving
  // within the group: by keyboard (relatedTarget) or by a pointer pressed on an
  // option, which Safari reports with no relatedTarget.
  const handleGroupBlur = (event) => {
    if (!isInProgressiveChain) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    if (isPointerDownInGroup.current) return;
    commitOtherAnswer();
  };

  const handleSelection = ({ optionId }) => {
    hasCommittedOther.current = false;
    // Emptying the Other text makes the radio group deselect with no option.
    // In a chain that is "not answered yet", not a blank answer, so the
    // question stays open for the customer to type again.
    if (isInProgressiveChain && optionId === undefined) {
      batch(() => {
        dispatch(clearSelectedOptionsAndValue({ componentId }));
        dispatch(clearConstraintsValidations({ componentId }));
      });
      return;
    }
    batch(() => {
      dispatch(
        clearSelectedOptionsAndValue({
          componentId,
        })
      );
      dispatch(
        clearConstraintsValidations({ componentId: component.component.id })
      );
      dispatch(
        selectOptionAndUpdateValue({
          componentId,
          optionId,
        })
      );
      if (optionId !== 'other-option') {
        triggerSelection();
        batch(() => {
          dispatch(
            setComponentValidationStatus({
              componentId,
              iFrameId,
              formId,
            })
          );
          dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
        });
      } else if (
        optionId === 'other-option' &&
        otherValue &&
        !isInProgressiveChain
      ) {
        // Outside a chain a re-picked Other with text validates at once. In a
        // chain that would collapse the question before the text field opens
        // after Edit, so the text is committed on Enter or blur instead.
        validateAnswer();
      }
    });
  };

  const handleEdit = () => {
    batch(() => {
      dispatch(clearSelectedOptionsAndValue({ componentId }));
      dispatch(clearConstraintsValidations({ componentId }));
      dispatch(reopenField({ page: component.component.page, componentId }));
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      commitOtherAnswer();
      const firstEditableComponentOnNextPage = getFirstEditableComponentOnPage(
        component.component.page + 1,
        components,
        pages
      );

      if (
        (isIos || isAndroid) &&
        ComponentTypesToReceiveFocusOnPageEnter.includes(
          firstEditableComponentOnNextPage?.input?.type
        )
      ) {
        dispatch(
          setComponentIdToReceiveFocus({
            componentIdToReceiveFocus:
              firstEditableComponentOnNextPage.component.id,
          })
        );
      }

      triggerSelection();
    }
  };

  useEffect(() => {
    options.forEach((option) => {
      if (option.is_checked_by_default) {
        dispatch(
          selectOptionAndUpdateValue({
            componentId,
            optionId: option.id,
          })
        );
        // Remove the default checked status of this option for the lifetime of the form.
        dispatch(
          setIsCheckedByDefault({
            componentId,
            isCheckedByDefault: false,
            optionId: option.id,
          })
        );
      }
    });
  }, []);

  selectedOptions = selectedOptions || [];

  useEffect(() => {
    if (selectedOptions.length > 0 && selectionCount > 0 && autoAdvance) {
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [selectionCount]);

  useEffect(() => {
    if (s3EnvDomain !== process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL) {
      setS3EnvDomain(process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL);
    }
  });

  if (hiddenInChameleon === true) {
    return null;
  }

  const mustacheContext = {
    components,
    currentComponent: component,
    iFrameId,
    formId,
    supplierMatches,
    showSmartMatchMeter,
    showSmartMatchMeterV2,
  };
  const heading = renderMustache({ template: text, ...mustacheContext });

  // In a progressive chain an answered question collapses to one line so the
  // customer can see what they chose and change it without losing later answers.
  if (isCollapsed) {
    return (
      <Box css={styles.answeredLine} data-testid="radioAnsweredLine">
        <Box css={styles.answeredText}>
          <Box css={styles.answeredQuestion}>{heading}</Box>
          <Box css={styles.answeredValue} data-testid="radioAnsweredValue">
            {getSelectedOptionLabel({ options, selectedOptions, otherValue })}
          </Box>
        </Box>
        <button
          type="button"
          css={styles.editButton}
          onClick={handleEdit}
          aria-label={`${editLabel}: ${heading}`}
          data-testid="radioAnsweredEdit"
        >
          <EditIcon />
        </button>
      </Box>
    );
  }

  const optionsWithoutTextLinks = options.filter(
    (option) => !option.display_as_text_link
  );

  const textLinkOption = options.find((option) => option.display_as_text_link);

  const selectedOption = String(selectedOptions[0]);
  const columnCount = component?.component?.additionalSettings?.column_count;

  return (
    <>
      <QuestionText
        heading={heading}
        subheading={renderMustache({
          template: subheading,
          ...mustacheContext,
        })}
      />
      <Box
        css={styles.radioContainer}
        data-testid="radioContainer"
        onPointerDown={handleGroupPointerDown}
        onPointerUp={handleGroupPointerUp}
        onPointerCancel={handleGroupPointerUp}
        onBlur={handleGroupBlur}
      >
        <RadioGroup
          defaultSelectedId={selectedOption}
          onChange={(optionId) => {
            handleSelection({ optionId });
          }}
          experimentName={radioGroupExperimentName}
        >
          <Layout
            experimentName={layoutExperimentName}
            columns={isTiled ? undefined : columnCount}
          >
            {optionsWithoutTextLinks.map((option) => {
              const { value, key, id, subtext } = option;
              const optionIdString = String(id);
              const isOtherOption = id === 'other-option';
              const optionText = isOtherOption ? key : value;
              const optionSubtext = isOtherOption
                ? component?.component?.additionalSettings?.other_option_subtext
                : subtext;

              const strippedUrl = swapS3DomainWithPublicAssetsDomain(
                option.icon_url,
                process.env.NEXT_PUBLIC_ASSETS_URL
              );
              const iconHeight = getIconHeight(isTiled, columnCount);
              const Icon = allOptionsHaveIcons ? (
                <img
                  style={{
                    maxWidth: '100%',
                    height: iconHeight,
                  }}
                  src={strippedUrl}
                  alt="optionImage"
                />
              ) : undefined;

              const optionComponent = isTiled ? (
                <IconTileRadioButton
                  Icon={Icon}
                  id={optionIdString}
                  key={optionIdString}
                  text={isOtherOption ? key : value}
                  otherInputValue={isOtherOption ? otherValue : undefined}
                  isOtherOption={isOtherOption}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  experimentName={iconTileRadioButtonExperimentName}
                  subtext={optionSubtext}
                  minHeight={tallestOptionHeight}
                />
              ) : (
                <RadioButton
                  Icon={Icon}
                  id={optionIdString}
                  key={optionIdString}
                  text={optionText}
                  otherInputValue={isOtherOption ? otherValue : undefined}
                  isOtherOption={isOtherOption}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  experimentName={radioButtonExperimentName}
                  smallIcon={columnCount > 1 && !isTiled}
                  subtext={optionSubtext}
                  minHeight={tallestOptionHeight}
                />
              );

              return (
                <div
                  key={optionIdString}
                  style={{
                    height: '100%',
                  }}
                  ref={(el) => {
                    optionRefs.current[optionIdString] = el;
                  }}
                >
                  {optionComponent}
                </div>
              );
            })}
          </Layout>
        </RadioGroup>
        {textLinkOption && (
          <Box css={styles.textLinkContainer} data-testid="textLinkOption">
            <Link
              component="button"
              underline="always"
              onClick={(event) => {
                event.preventDefault();
                handleSelection({ optionId: String(textLinkOption.id) });
                triggerSelection();
              }}
              css={styles.textLink}
              onKeyDown={handleKeyPress}
            >
              {textLinkOption.value}
            </Link>
          </Box>
        )}
      </Box>
    </>
  );
};

export default RadioInput;
